import { Modal, notification } from 'ant-design-vue'
import i18n from '@/i18n'
import { app } from '@/main'

/*
Modal 說明：
1.用法同ant design vue。
2.常用欄位：
title-标题。
content-内容。
width-宽度(預設420px)。
keyboard-是否支持键盘esc关闭(針對不同類型modal，預設不同)。
maskClosable-点击蒙层是否允许关闭(針對不同類型modal，預設不同)。
icon-图标 Icon 类型(預設confirmModal為exclamation-circle，其他維持原樣式)。
okText-确认按钮文字(針對不同類型modal，預設文字不同)。
cancelText-取消按钮文字(預設"取消"文字)。
onOk-点击确定回调，参数为关闭函数，返回 promise 时 resolve 后自动关闭。
onCancel-取消回调，参数为关闭函数，返回 promise 时 resolve 后自动关闭。
*/

//判斷目前modal是否被開啟 (避免跳兩次)
let modalHasOpened = false

//Modal相關button設定
let modalInit = {
  width: '420px',
  confirm: {
    keyboard: true,
    maskClosable: true
  },
  success: {
    keyboard: false,
    maskClosable: false
  },
  info: {
    keyboard: true,
    maskClosable: true
  },
  warning: {
    keyboard: false,
    maskClosable: false
  },
  error: {
    keyboard: false,
    maskClosable: false
  }
}

function hasOpened() {
  if (!modalHasOpened) {
    modalHasOpened = true
    return false
  }
  return true
}

//Modal AfterClose 建構式
function AfterClose(config) {
  this.onOk = () => {
    modalHasOpened = false //更改裝態為關閉
    if (typeof config.onOk === 'function') config.onOk()
  }
  this.onCancel = () => {
    modalHasOpened = false //更改裝態為關閉
    if (typeof config.onCancel === 'function') config.onCancel()
  }
}

//Modal相關config處理
let modalConfig = config => {
  let afterClose = new AfterClose(config)
  let obj = {
    ...config,
    ...afterClose
  }
  return obj
}

//Modal(confirm)
export const confirmModal = config => {
  hasOpened() ||
    Modal.confirm({
      keyboard: modalInit.confirm.keyboard,
      maskClosable: modalInit.confirm.maskClosable,
      width: modalInit.width,
      icon: 'exclamation-circle',
      okText: i18n.t('i18n_confirm'),
      cancelText: i18n.t('i18n_cancel'),
      ...modalConfig(config)
    })
}

//Modal(success)
export const successModal = config => {
  hasOpened() ||
    Modal.success({
      keyboard: modalInit.success.keyboard,
      maskClosable: modalInit.success.maskClosable,
      width: modalInit.width,
      okText: i18n.t('i18n_confirm'),
      ...modalConfig(config)
    })
}

//Modal(info)
export const infoModal = config => {
  hasOpened() ||
    Modal.info({
      keyboard: modalInit.info.keyboard,
      maskClosable: modalInit.info.maskClosable,
      width: modalInit.width,
      okText: i18n.t('i18n_confirm'),
      ...modalConfig(config)
    })
}

//Modal(warning)
export const warningModal = config => {
  hasOpened() ||
    Modal.warning({
      keyboard: modalInit.warning.keyboard,
      maskClosable: modalInit.warning.maskClosable,
      width: modalInit.width,
      okText: i18n.t('i18n_confirm'),
      ...modalConfig(config)
    })
}

//Modal(error)
export const errorModal = config => {
  hasOpened() ||
    Modal.error({
      keyboard: modalInit.error.keyboard,
      maskClosable: modalInit.error.maskClosable,
      width: modalInit.width,
      okText: i18n.t('i18n_confirm'),
      ...modalConfig(config)
    })
}

// custom info modal for upload result
/**
 * @param {Object} config modal config, same as infoModal
 * @param {Array} uploadResult Array object,  [{ is_successful:Boolean, is_failed: Boolean, err_msg: String }]
 * @param {String} field 欲新增的欄位為何, e.g. 收幣地址
 * @param {String} messageTitle
 * @param {String} messageDetail
 */

export const infoUploadModal = (
  uploadResult,
  field,
  config = {},
  messageTitle = 'i18n_upload_tip_success',
  messageDetail = 'i18n_upload_tip_following_error'
) => {
  // h 被隐式调用，定义时不可省略或更名 ref: https://github.com/vueComponent/ant-design-vue/issues/1183
  const h = app.$createElement
  const successCount = uploadResult.filter(result => !!result.is_successful)
    .length
  const failCount = uploadResult.filter(result => !!result.is_failed).length

  infoModal({
    title: (
      <div>
        <div>{app.$t(messageTitle, { count: successCount, field })}</div>
        {failCount ? (
          <div>
            <div>{app.$t(messageDetail)}</div>
            <div style="max-height: 250px; overflow-y: scroll;">
              {uploadResult.map(result =>
                result.is_failed ? <div>{result.err_msg}</div> : null
              )}
            </div>
          </div>
        ) : null}
      </div>
    )
  })
}

/*
notification 說明：
1.用法同ant design vue。
2.常用欄位：
message-通知提醒标题，必选。
description-通知提醒内容，必选。
duration-默认自动关闭延时，单位秒(目前統一預設不自動關閉)。
onClose-点击默认关闭按钮时触发的回调函数。
*/

//notification自動關閉設定
let duration = null

//notification(default)
export const notify = config => {
  notification.open({
    duration: duration,
    ...config
  })
}

//notification(success)
export const successNotify = config => {
  notification.success({
    duration: duration,
    ...config
  })
}

//notification(info)
export const infoNotify = config => {
  notification.info({
    duration: duration,
    ...config
  })
}

//notification(warning)
export const warningNotify = config => {
  notification.warning({
    duration: duration,
    ...config
  })
}

//notification(error)
export const errorNotify = config => {
  notification.error({
    duration: duration,
    ...config
  })
}
